/**
 * Channel helpers (ADR-025 multi-channel messaging UX).
 *
 * A "channel" is the transport a thread travelled over, independent of the
 * provider that carried it (nexmo / twilio / messagemedia are all `sms`).
 */
import { isWebchatMobile } from './format';

export type Channel = 'sms' | 'whatsapp' | 'messenger' | 'webchat' | 'email';

export interface ChannelConfig {
  label: string;
  /** Max characters the composer should allow; null = unbounded. */
  charLimit: number | null;
  hasScheduling: boolean;
  hasLinkShortener: boolean;
}

export const CHANNEL_CONFIG: Record<Channel, ChannelConfig> = {
  sms: { label: 'SMS', charLimit: 640, hasScheduling: true, hasLinkShortener: true },
  whatsapp: { label: 'WhatsApp', charLimit: 4096, hasScheduling: false, hasLinkShortener: true },
  messenger: { label: 'Messenger', charLimit: 2000, hasScheduling: false, hasLinkShortener: false },
  webchat: { label: 'Web Chat', charLimit: null, hasScheduling: false, hasLinkShortener: false },
  email: { label: 'Email', charLimit: null, hasScheduling: false, hasLinkShortener: false },
};

export const CHANNELS: Channel[] = ['sms', 'whatsapp', 'messenger', 'webchat', 'email'];

const SMS_PROVIDERS = ['twilio', 'nexmo', 'messagemedia'];

/**
 * Map a botmap/provider type to its channel. Returns null for unknown types
 * so callers can fall back rather than mislabel.
 */
export function channelFromBotmapType(type?: string | null): Channel | null {
  const t = String(type || '').trim().toLowerCase();
  if (!t) return null;
  if (SMS_PROVIDERS.includes(t)) return 'sms';
  if (t === 'whatsapp') return 'whatsapp';
  if (t === 'messenger') return 'messenger';
  if (t === 'webchat') return 'webchat';
  return null;
}

/**
 * Normalise any channel-ish string (canonical channel name or a provider
 * name) to a Channel, or null when it is not recognised.
 */
export function normaliseChannel(value?: string | null): Channel | null {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return null;
  if ((CHANNELS as string[]).includes(v)) return v as Channel;
  if (v === 'facebook') return 'messenger';
  if (v === 'web_chat' || v === 'web-chat' || v === 'web') return 'webchat';
  if (v === 'mail') return 'email';
  return channelFromBotmapType(v);
}

/**
 * Channel a thread row travelled over. Precedence:
 *   1. `thread.channel` (set per-message by the backend)
 *   2. `thread.conversation.channel`
 *   3. derived from `thread.conversation.botmap.type`
 */
export function threadChannel(thread: any): Channel | null {
  if (!thread) return null;
  return (
    normaliseChannel(thread.channel)
    || normaliseChannel(thread.conversation?.channel)
    || channelFromBotmapType(thread.conversation?.botmap?.type)
  );
}

export interface ContactChannel {
  type: Channel;
  botmapId?: string;
  conversationId?: string | null;
  /** Phone number / page-scoped id / email the contact is reachable at. */
  address?: string;
  /** Last inbound message on this channel (drives the WhatsApp 24h window). */
  lastInboundAt?: string | null;
  windowExpiresAt?: string | null;
}

const toContactChannel = (raw: any): ContactChannel | null => {
  if (!raw) return null;
  const type = normaliseChannel(raw.type || raw.channel);
  if (!type) return null;
  return {
    type,
    botmapId: raw.botmapId ?? raw.botmap_id ?? undefined,
    conversationId: raw.conversationId ?? raw.conversation_id ?? null,
    address: raw.address ?? raw.mobile ?? raw.number ?? undefined,
    lastInboundAt: raw.lastInboundAt ?? raw.last_inbound_at ?? null,
    windowExpiresAt: raw.windowExpiresAt ?? raw.window_expires_at ?? null,
  };
};

/**
 * Channels the contact can currently be reached on.
 *
 * Prefers the backend-provided `contactModel.channels` array (deduplicated by
 * type, first wins). Otherwise derives from the legacy flat fields: `mobile`
 * (a real phone number, not a webchat UUID) -> sms, `whatsapp` -> whatsapp.
 */
export function getAvailableChannels(contactModel: any): ContactChannel[] {
  if (!contactModel) return [];

  if (Array.isArray(contactModel.channels)) {
    const seen = new Set<Channel>();
    const out: ContactChannel[] = [];
    contactModel.channels.forEach((raw: any) => {
      const ch = toContactChannel(raw);
      if (ch && !seen.has(ch.type)) {
        seen.add(ch.type);
        out.push(ch);
      }
    });
    return out;
  }

  const out: ContactChannel[] = [];
  const mobile = contactModel.mobile ? String(contactModel.mobile).trim() : '';
  if (mobile && !isWebchatMobile(mobile)) {
    out.push({ type: 'sms', address: mobile });
  }
  const whatsapp = contactModel.whatsapp ? String(contactModel.whatsapp).trim() : '';
  if (whatsapp) {
    out.push({
      type: 'whatsapp',
      address: whatsapp,
      lastInboundAt: contactModel.whatsapp_last_inbound_at ?? contactModel.whatsappLastInboundAt ?? null,
    });
  }
  return out;
}
